import React, { ChangeEvent, useState } from "react";
import { Box, Button, FormHelperText, TextField } from "@mui/material";
import { ChangeDocument, DocumentFile } from "../../models/machineDocumentsModel";
import colors from "../../../../shared/themes/colors";
import moment from "moment";
import {
  FieldArrayWithId,
  FieldErrors,
  UseFieldArrayAppend,
  UseFieldArrayRemove,
  UseFormClearErrors,
  UseFormRegister,
  UseFormSetValue
} from "react-hook-form";
import DocumentFileItem from "./DocumentFileItem";
import { MAX_FILE_SIZE_MB } from "../../../../shared/models/models";
import useTranslation from "../../../../shared/hooks/useTranslation";

interface UploadDocumentProps {
  register: UseFormRegister<{
    documents: ChangeDocument[];
  }>;
  errors: FieldErrors<{
    documents: ChangeDocument[];
  }>;
  clearErrors?: UseFormClearErrors<{
    documents: ChangeDocument[];
  }>;
  setValue: UseFormSetValue<{
    documents: ChangeDocument[];
  }>;
  fields: FieldArrayWithId<
    {
      documents: ChangeDocument[];
    },
    "documents",
    "id"
  >[];
  append: UseFieldArrayAppend<{
    documents: ChangeDocument[];
  }>;
  remove: UseFieldArrayRemove;
  maxFileSizeMb?: number;
}

const UploadDocument = ({
  maxFileSizeMb = MAX_FILE_SIZE_MB,
  register,
  errors,
  clearErrors,
  fields,
  append,
  remove,
  setValue
}: UploadDocumentProps) => {
  const [error, setError] = useState<string>();
  const [canDrop, setCanDrop] = useState(false);
  const { translate } = useTranslation("myMachinery");
  const { translate: tCommon } = useTranslation("common");

  const handleFileDrop = (event: any) => {
    event.preventDefault();
    handleFileChange(event.dataTransfer.files);
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files ? Array.from(event.target.files) : [];

    selectedFiles.forEach((file, i) => {
      processFile(file, i);
    });

    event.target.value = "";
  };

  const processFile = async (file: File, index: number) => {
    setError(undefined);

    if (file.size > maxFileSizeMb * 1024 * 1024) {
      setValue("documents", []);
      setError(tCommon("uploadFile.fileIsTooBig"));
      return;
    }

    const url = await detectUrl(file);

    if (url) {
      const newFile: DocumentFile = {
        name: file.name,
        content: url,
        size: file.size,
        createDate: moment().toDate()
      };

      const newChangeDocument: ChangeDocument = { name: "", file: newFile };
      append(newChangeDocument);
    }
  };

  const detectUrl = (file: Blob | null) => {
    return new Promise<string | null>((resolve, reject) => {
      if (!file) {
        resolve(null);
        return;
      }
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleDelete = (fileIndex: number) => {
    remove(fileIndex);
  };

  return (
    <>
      <Box
        className={`upload-file w-full ${canDrop && "active"} ${!!error && "error"}`}
        sx={{
          borderColor: !!error ? colors.primary.red : ""
        }}
        onDrop={(event) => {
          handleFileDrop(event);
          setCanDrop(false);
        }}
        onDragOver={(event) => {
          event.preventDefault();
          setCanDrop(true);
        }}
        onDragLeave={() => setCanDrop(false)}
      >
        <p className="text-uppercase text-center text-12-regular">{tCommon("uploadFile.dragAndDrop")}</p>
        <p className="text-uppercase text-center text-12-regular">{tCommon("uploadFile.or")}</p>

        <label htmlFor="file-input">
          <Button component="span" variant="outlined">
            {translate("uploadFileButton")}
          </Button>
        </label>
        {!!error && (
          <FormHelperText
            error
            sx={{
              padding: "6px 8px",
              margin: 0,
              textAlign: "center",
              minHeight: "40px",
              maxWidth: "370px"
            }}
          >
            {error}
          </FormHelperText>
        )}

        <Box className="text-12-regular text-center">{tCommon("uploadFile.supportedFormats")}</Box>
      </Box>

      <Box>
        <TextField
          type="file"
          id="file-input"
          sx={{ display: "none" }}
          inputProps={{ multiple: true }}
          onChange={handleFileChange}
        />

        {fields && fields?.length > 0 && (
          <Box className="flex flex-col gap-2" sx={{ mt: 1 }}>
            {fields?.map((file, i) => (
              <DocumentFileItem
                key={i}
                document={file.file}
                documentIndex={i}
                register={register}
                setValue={setValue}
                clearErrors={clearErrors}
                errors={errors}
                isCreate={true}
                onDelete={() => handleDelete(i)}
              />
            ))}
          </Box>
        )}
      </Box>
    </>
  );
};

export default UploadDocument;